import React, { useState, useEffect } from 'react';
import {
    FaHistory,
    FaSearch,
    FaFilter,
    FaUser,
    FaFileAlt,
    FaCalendarAlt,
    FaUndo,
    FaCheckCircle,
    FaClock,
    FaExclamationCircle,
    FaTimes,
    FaWallet
} from 'react-icons/fa';
import * as adminService from '../services/adminService';
import Toast from '../components/Toast';

const AdminPaymentManagement = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [toast, setToast] = useState(null);

    // Refund Modal State
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [refundAmount, setRefundAmount] = useState('');
    const [isRefunding, setIsRefunding] = useState(false);

    const loadPayments = React.useCallback(async () => {
        setLoading(true);
        try {
            const response = await adminService.getAllPayments();
            if (response.success) {
                setPayments(response.data || []);
            }
        } catch (error) {
            showToast(error.message || 'Failed to load payments', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPayments();
    }, [loadPayments]);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const handleRefundClick = (payment) => {
        setSelectedPayment(payment);
        setRefundAmount(payment.amount); // Default to full refund
        setShowRefundModal(true);
    };

    const handleConfirmRefund = async () => {
        if (!selectedPayment) return;

        setIsRefunding(true);
        try {
            const response = await adminService.initiateRefund(selectedPayment.id, refundAmount);
            if (response.success) {
                showToast('Refund processed successfully', 'success');
                setShowRefundModal(false);
                loadPayments(); // Reload list
            }
        } catch (error) {
            showToast(error.message || 'Refund failed', 'error');
        } finally {
            setIsRefunding(false);
        }
    };

    const filteredPayments = payments.filter(payment => {
        const matchesSearch =
            payment.user?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            payment.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            payment.razorpay_order_id?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    const getStatusStyle = (status) => {
        switch (status) {
            case 'PAID':
                return 'bg-green-100 text-green-700 border-green-200';
            case 'REFUNDED':
                return 'bg-orange-100 text-orange-700 border-orange-200';
            case 'PENDING':
                return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'FAILED':
                return 'bg-red-100 text-red-700 border-red-200';
            default:
                return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const darkGradient = {
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-gray-500 font-medium">Loading payments...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-12">
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            {/* Header Section */}
            <div className="py-12 px-4 sm:px-6 lg:px-8" style={darkGradient}>
                <div className="max-w mx-auto">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div>
                            <h1 className="text-3xl font-extrabold text-white flex items-center gap-3">
                                <FaWallet className="text-emerald-400" />
                                Payment Management
                            </h1>
                            <p className="mt-2 text-blue-100 text-lg">
                                Monitor transactions and manage student refunds
                            </p>
                        </div>
                        <div className="flex items-center gap-4 bg-white/10 p-4 rounded-xl backdrop-blur-sm border border-white/20">
                            <div className="text-right">
                                <p className="text-blue-100 text-xs font-semibold uppercase tracking-wider">Total Revenue</p>
                                <p className="text-2xl font-bold text-white">
                                    ₹{payments.filter(p => p.status === 'PAID').reduce((sum, p) => sum + Number(p.amount), 0).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
                {/* Filters & Search */}
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-gray-100">
                    <div className="flex flex-col lg:flex-row gap-6">
                        <div className="flex-grow relative">
                            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name, email, or order ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all outline-none"
                            />
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl">
                                <FaFilter className="text-gray-400" />
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="bg-transparent border-none focus:ring-0 font-semibold text-gray-700 outline-none"
                                >
                                    <option value="all">All Statuses</option>
                                    <option value="PAID">Paid</option>
                                    <option value="REFUNDED">Refunded</option>
                                    <option value="PENDING">Pending</option>
                                    <option value="FAILED">Failed</option>
                                </select>
                            </div>
                            <button
                                onClick={loadPayments}
                                className="p-3 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-all"
                                title="Refresh Data"
                            >
                                <FaHistory />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Payments Table */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Student Details</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Article Title</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Date & Time</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Order ID</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredPayments.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                            No payments found matching your criteria.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredPayments.map((payment) => (
                                        <tr key={payment.id} className="hover:bg-gray-50/50 transition-all">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600">
                                                        <FaUser />
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-gray-900">{payment.user?.full_name}</p>
                                                        <p className="text-xs text-gray-500">{payment.user?.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2 max-w-xs">
                                                    <FaFileAlt className="text-gray-400 flex-shrink-0" />
                                                    <p className="text-sm font-medium text-gray-700 truncate" title={payment.young_india_article?.title || payment.purpose}>
                                                        {payment.purpose === 'SIGNUP_FEE' ? 'Registration Signup Fee' : (payment.young_india_article?.title || payment.purpose || 'Pre-payment Order')}
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="text-sm font-bold text-gray-900">₹{Number(payment.amount).toLocaleString()}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                                    <FaCalendarAlt />
                                                    {formatDate(payment.created_at)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <code className="text-[10px] bg-gray-100 px-2 py-1 rounded text-gray-600">
                                                    {payment.razorpay_order_id}
                                                </code>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold border ${getStatusStyle(payment.status)}`}>
                                                    {payment.status === 'PAID' && <FaCheckCircle className="mr-1" />}
                                                    {payment.status === 'PENDING' && <FaClock className="mr-1" />}
                                                    {payment.status === 'FAILED' && <FaExclamationCircle className="mr-1" />}
                                                    {payment.status === 'REFUNDED' && <FaUndo className="mr-1" />}
                                                    {payment.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                {/* Refund button commented out
                                                {payment.status === 'PAID' ? (
                                                    <button
                                                        onClick={() => handleRefundClick(payment)}
                                                        className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition-all font-bold text-xs"
                                                    >
                                                        <FaUndo />
                                                        Refund
                                                    </button>
                                                ) : (
                                                    <span className="text-xs text-gray-400 font-medium italic">
                                                        No Actions
                                                    </span>
                                                )}
                                                */}
                                                <span className="text-xs text-gray-400 font-medium italic">
                                                    No Actions
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Refund Confirmation Modal */}
            {showRefundModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 text-center border-b border-gray-100">
                            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaUndo className="text-2xl text-orange-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900">Initiate Refund</h3>
                            <p className="text-gray-500 mt-1 italic">Process a refund for {selectedPayment?.user?.full_name}</p>
                        </div>

                        <div className="p-8">
                            <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm text-gray-500">Original Payment:</span>
                                    <span className="text-sm font-bold text-gray-900">₹{Number(selectedPayment?.amount).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">Order ID:</span>
                                    <span className="text-xs text-gray-700 font-bold font-mono">{selectedPayment?.razorpay_order_id}</span>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Refund Amount (INR)</label>
                                    <div className="relative">
                                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-bold">₹</div>
                                        <input
                                            type="number"
                                            value={refundAmount}
                                            onChange={(e) => setRefundAmount(e.target.value)}
                                            className="w-full pl-8 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all outline-none font-bold"
                                            max={selectedPayment?.amount}
                                        />
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-2">Maximum refundable: ₹{selectedPayment?.amount}</p>
                                </div>

                                <div className="flex gap-4 pt-4">
                                    <button
                                        onClick={() => setShowRefundModal(false)}
                                        className="flex-1 px-6 py-4 bg-gray-100 text-gray-600 rounded-2xl font-bold hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
                                    >
                                        <FaTimes />
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleConfirmRefund}
                                        disabled={isRefunding || !refundAmount || refundAmount <= 0}
                                        className="flex-1 px-6 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl font-bold hover:from-orange-600 hover:to-red-600 transition-all shadow-lg hover:shadow-orange-500/25 disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        {isRefunding ? 'Processing...' : (
                                            <><FaUndo /> Confirm Refund</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPaymentManagement;
